import React, { useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import axios from 'axios';

import PageContainer from '../../../components/container/PageContainer';
import SingleAddTable from '../../../components/table/SingleAddTable';
import DeleteModal from '../../../components/modal/DeleteModal';
import AddButton from '../../../components/button/AddButton';
import LoadingOverlay from '../../../components/LoadingOverlay';
import BackToListButton from '../../../components/button/BackToListButton';

import {
  ASSIGNMENT_HEADER,
  ASSIGNMENT_DELETE_FIELDS,
} from '../../../constants/assignment';
import { VALIDATION_TEMPLATE } from '../../../constants/validation/validationTemplete';

import {
  AssignmentRow,
  AssignmentGetResponse,
  EmptyAssignmentGetResponse,
} from '../../../types/assignment/AssignmentGetResponse';
import { AssignmentPostRequest } from '../../../types/assignment/AssignmentPostRequest';
import { EmptyAssignmentDeleteItem } from '../../../types/assignment/AssignmentDeleteItem';
import {
  NewAssignmentSelection,
  EmptyNewAssignmentSelection,
} from '../../../types/assignment/NewAssignmentSelection';

import {
  createAssignment,
  deleteAssignment,
  getAssignments,
} from '../../../services/api';

import AddAssignmentRow from './AddAssignmentRow';
import RenderAssignmentRow from './RenderAssignmentRow';
import SyncedScrollHeader from '../../../components/layout/SyncedScrollHeader';
import '../../../styles/AssigmentsandCostHeader.css';

// 共通エラーメッセージ整形
const getErrMsg = (e: unknown, fallback: string) => {
  if (axios.isAxiosError(e)) {
    return (e.response?.data as any)?.message ?? e.message ?? fallback;
  }
  if (e instanceof Error) return e.message || fallback;
  return fallback;
};

const Assignments = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const pid = Number(projectId);
  const queryClient = useQueryClient();

  const [isAdding, setIsAdding] = useState(false);
  const [deletingItem, setDeletingItem] = useState<AssignmentRow>(
    EmptyAssignmentDeleteItem
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selected, setSelected] = useState<NewAssignmentSelection>(
    EmptyNewAssignmentSelection
  );

  // 表の横スクロール（ヘッダー追従用）
  const tableRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);

  // 一覧取得（items + project）
  const {
    data = EmptyAssignmentGetResponse,
    isLoading,
    isError,
    error,
  } = useQuery<AssignmentGetResponse>({
    queryKey: ['assignments', pid],
    queryFn: () => getAssignments(pid),
    initialData: EmptyAssignmentGetResponse,
  });

  useEffect(() => {
    if (isError) {
      alert(getErrMsg(error, '参画社員一覧の取得に失敗しました'));
    }
  }, [isError, error]);

  // 追加
  const postRequest = useMutation({
    mutationFn: (req: AssignmentPostRequest) => createAssignment(pid, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assignments', pid] });
      setIsAdding(false);
      setSelected(EmptyNewAssignmentSelection);
    },
    onError: (e) => alert(getErrMsg(e, '参画社員の追加に失敗しました')),
  });

  // 削除
  const delRequest = useMutation({
    mutationFn: (id: number) => deleteAssignment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assignments', pid] });
      setDeletingItem(EmptyAssignmentDeleteItem);
    },
    onError: (e) => alert(getErrMsg(e, '参画社員の削除に失敗しました')),
  });

  const onAddButtonClick = () => setIsAdding(true);

  // 追加の必須チェック（テンプレ利用）
  const onAddSubmitButtonClick = () => {
    if (!selected.employeeId) {
      alert(VALIDATION_TEMPLATE.required('社員'));
      return;
    }
    postRequest.mutate({ employeeId: selected.employeeId });
  };

  const onSubmitCancelButtonClick = () => {
    setIsAdding(false);
    setSelected(EmptyNewAssignmentSelection);
  };

  const onDeleteButtonClick = (item: AssignmentRow) => {
    setDeletingItem(item);
    setShowDeleteModal(true);
  };

  const isMutating = [postRequest, delRequest].some(
    (m) => m.status === 'pending'
  );

  return (
    <PageContainer
      title="参画社員管理"
      subtitle={data.project?.projectName ?? ''}
    >
      <LoadingOverlay isLoading={isLoading} isMutating={isMutating} />

      <SyncedScrollHeader
        targetRef={tableRef}
        className="mb-3"
        outerRef={headerRef}
      >
        <Row className="header-row-assigmentcost gx-0">
          <Col className="header-fields-assigmentcost order-1"></Col>

          <Col xs="auto" className="header-right-assigmentcost order-2">
            <div className="actions-group-assigmentcost">
              <div className="actions-searchreset-assigmentcost"></div>
              <div className="actions-extra-assigmentcost">
                <AddButton onClick={onAddButtonClick} buttonLabel="参画社員" />
              </div>
            </div>
          </Col>
        </Row>
      </SyncedScrollHeader>

      <SingleAddTable<AssignmentRow>
        headers={ASSIGNMENT_HEADER}
        items={data.items ?? []}
        actionable
        scrollContainerRef={tableRef}
        headerRef={headerRef}
        footerRef={footerRef}
        renderItem={(item) => (
          <RenderAssignmentRow item={item} onDelete={onDeleteButtonClick} />
        )}
        addItem={() => (
          <AddAssignmentRow
            selected={selected}
            setSelected={setSelected}
            onAddSubmitButtonClick={onAddSubmitButtonClick}
            onSubmitCancelButtonClick={onSubmitCancelButtonClick}
          />
        )}
        isAdding={isAdding}
        setIsAdding={setIsAdding}
        editItem={() => <></>}
        editingId={null}
      />

      <BackToListButton
        label="プロジェクト一覧に戻る"
        to="/admin/projects"
        ref={footerRef}
      />

      {showDeleteModal && (
        <DeleteModal<AssignmentRow>
          show={showDeleteModal}
          item={deletingItem}
          fields={ASSIGNMENT_DELETE_FIELDS}
          onClose={() => {
            setShowDeleteModal(false);
            setDeletingItem(EmptyAssignmentDeleteItem);
          }}
          onDelete={() => {
            delRequest.mutate(deletingItem.id);
            setShowDeleteModal(false);
          }}
        />
      )}
    </PageContainer>
  );
};

export default Assignments;
